
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft } from "lucide-react";
import { HormonalFlowchart } from "./HormonalFlowchart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

// Cores específicas para cada hormônio
const hormoneColors = {
  testosterona: "#FF6B6B",
  estradiol: "#4ECDC4",
  lh: "#45B7D1",
  fsh: "#96CEB4",
  progesterona: "#FFEEAD"
};

// Valores de referência por gênero
const hormoneReferences = {
  Masculino: {
    testosterona: { min: 240, max: 950, unit: "ng/dL" },
    estradiol: { min: 10, max: 40, unit: "pg/mL" },
    lh: { min: 1.5, max: 9.3, unit: "mUI/mL" },
    fsh: { min: 1.6, max: 8.0, unit: "mUI/mL" }
  },
  Feminino: {
    estradiol: { min: 30, max: 400, unit: "pg/mL" },
    lh: { min: 2.4, max: 12.6, unit: "mUI/mL" },
    fsh: { min: 3.5, max: 12.5, unit: "mUI/mL" },
    progesterona: { min: 0.2, max: 28, unit: "ng/mL" }
  }
};

export default function BasicCalculator({ onBack, gender, onResultsUpdate }) {
  const { toast } = useToast();
  const [examValues, setExamValues] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  // Função para obter referência hormonal
  const getHormoneReference = (hormone) => {
    if (!hormone) return { min: 0, max: 0, unit: "" };
    const references = hormoneReferences[gender] || {};
    return references[hormone.toLowerCase()] || { min: 0, max: 0, unit: "" };
  };

  // Função para determinar status do hormônio
  const getHormoneStatus = (value, reference) => {
    if (!reference || !value) return "normal";
    if (value < reference.min) return "low";
    if (value > reference.max) return "high";
    return "normal";
  };

  // Função para obter cor baseada no status
  const getStatusColor = (status) => {
    switch (status) {
      case "low": return "#ef4444"; // vermelho
      case "high": return "#f59e0b"; // amarelo
      default: return "#4ade80"; // verde
    }
  };

  const handleExamSubmit = (e) => {
    e.preventDefault();
    
    if (Object.keys(examValues).length === 0) {
      toast({
        title: "Erro",
        description: "Por favor, preencha os valores dos exames",
        variant: "destructive",
      });
      return;
    }

    // Análise dos resultados
    const results = analyzeResults(examValues);
    
    setAnalysisResults(results);
    setShowResults(true);
    
    if (onResultsUpdate) onResultsUpdate(results);

    toast({
      title: "Análise Concluída",
      description: "Seus resultados estão prontos",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setExamValues(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  // Função para analisar os resultados
  const analyzeResults = (values) => {
    const results = {
      correlations: [],
      severity: "normal",
      recommendations: []
    };

    // Análise por gênero
    if (gender === "Masculino") {
      analyzeMaleHormones(values, results);
    } else {
      analyzeFemaleHormones(values, results);
    }

    return results;
  };

  // Análise hormonal masculina
  const analyzeMaleHormones = (values, results) => {
    const { testosterona, estradiol, lh, fsh } = values;
    const refs = hormoneReferences.Masculino;

    // Testosterona baixa
    if (testosterona < refs.testosterona.min) {
      results.severity = "high";
      results.correlations.push({
        title: "Testosterona Baixa",
        description: "Níveis reduzidos de testosterona podem indicar hipogonadismo",
        severity: "high",
        recommendation: "Avaliação endocrinológica recomendada"
      });
    }

    // Estradiol alto
    if (estradiol > refs.estradiol.max) {
      results.severity = results.severity === "normal" ? "moderate" : results.severity;
      results.correlations.push({
        title: "Estradiol Elevado",
        description: "Níveis elevados de estradiol podem indicar aromatização excessiva",
        severity: "moderate",
        recommendation: "Considerar avaliação da enzima aromatase"
      });
    }

    // LH e FSH alterados
    if (lh < refs.lh.min && fsh < refs.fsh.min) {
      results.severity = "high";
      results.correlations.push({
        title: "Hipogonadismo Secundário",
        description: "Padrão sugestivo de hipogonadismo hipogonadotrófico",
        severity: "high",
        recommendation: "Avaliação hipofisária recomendada"
      });
    }
  };

  // Análise hormonal feminina
  const analyzeFemaleHormones = (values, results) => {
    const { estradiol, lh, fsh, progesterona } = values;
    const refs = hormoneReferences.Feminino;

    // Estradiol baixo
    if (estradiol < refs.estradiol.min) {
      results.severity = "high";
      results.correlations.push({
        title: "Estradiol Baixo",
        description: "Níveis reduzidos de estradiol podem indicar hipoestrogenismo",
        severity: "high",
        recommendation: "Avaliação ginecológica recomendada"
      });
    }

    // LH e FSH elevados
    if (lh > refs.lh.max && fsh > refs.fsh.max) {
      results.severity = "high";
      results.correlations.push({
        title: "Hipogonadismo Primário",
        description: "Padrão sugestivo de falência ovariana",
        severity: "high",
        recommendation: "Avaliação ginecológica urgente"
      });
    }

    // Progesterona baixa
    if (progesterona < refs.progesterona.min) {
      results.severity = results.severity === "normal" ? "moderate" : results.severity;
      results.correlations.push({
        title: "Progesterona Baixa",
        description: "Níveis reduzidos de progesterona podem indicar anovulação",
        severity: "moderate",
        recommendation: "Monitoramento do ciclo menstrual"
      });
    }
  };

  // Preparar dados para o gráfico
  const getChartData = () => {
    if (!examValues || Object.keys(examValues).length === 0) return [];
    
    return Object.entries(examValues).map(([hormone, value]) => {
      const reference = getHormoneReference(hormone);
      return {
        name: hormone.toUpperCase(),
        value: value,
        min: reference.min,
        max: reference.max,
        unit: reference.unit,
        color: hormoneColors[hormone.toLowerCase()] || "#ff3366"
      };
    });
  };

  // Componente de tooltip customizado
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#1a1a2e]/95 p-4 rounded-lg border border-[#ff3366]/20 shadow-lg backdrop-blur-sm">
          <h4 className="text-[#ff3366] font-bold text-lg mb-2">{label}</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-white/80">Valor:</span>
              <span className="text-white font-semibold">
                {data.value} {data.unit}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/80">Referência:</span>
              <span className="text-white">
                {data.min}-{data.max} {data.unit}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/80">Status:</span>
              <span className={`px-2 py-1 rounded text-sm font-semibold ${
                data.value < data.min ? "text-red-400" :
                data.value > data.max ? "text-yellow-400" : "text-green-400"
              }`}>
                {data.value < data.min ? "Baixo" :
                 data.value > data.max ? "Alto" : "Normal"}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      className="mt-20 max-w-4xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center gap-4 mb-6">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-[#ff3366] hover:text-[#ff3366]/80 hover:bg-[#1a1a2e]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <h2 className="text-xl font-bold text-[#ff3366]">
          Calculadora Básica - {gender}
        </h2>
      </div>

      <form onSubmit={handleExamSubmit} className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          {gender === "Masculino" ? (
            <>
              <input
                type="number"
                name="testosterona"
                placeholder="Testosterona Total (ng/dL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="estradiol"
                placeholder="Estradiol (pg/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="lh"
                placeholder="LH (mUI/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="fsh"
                placeholder="FSH (mUI/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
            </>
          ) : (
            <>
              <input
                type="number"
                name="estradiol"
                placeholder="Estradiol (pg/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="lh"
                placeholder="LH (mUI/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="fsh"
                placeholder="FSH (mUI/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
              <input
                type="number"
                name="progesterona"
                placeholder="Progesterona (ng/mL)"
                onChange={handleInputChange}
                className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
                step="0.01"
                required
              />
            </>
          )}
        </div>

        <Button
          type="submit"
          className="w-full bg-[#ff3366] hover:bg-[#ff3366]/80 text-white"
        >
          Analisar Resultados
        </Button>
      </form>

      {showResults && analysisResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 space-y-6"
        >
          {/* Gráfico de Perfil Hormonal */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Perfil Hormonal
            </h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={getChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ff3366" opacity={0.1} />
                  <XAxis
                    dataKey="name"
                    stroke="#ff3366"
                    tick={{ fill: "#ffffff" }}
                  />
                  <YAxis
                    stroke="#ff3366"
                    tick={{ fill: "#ffffff" }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="value"
                    fill={(entry) => entry.color}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Fluxograma Hormonal */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Fluxograma do Eixo Hormonal
            </h3>
            <HormonalFlowchart 
              examValues={examValues}
              gender={gender}
            />
          </div>

          {/* Resultados da Análise */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Análise Detalhada
            </h3>
            
            {/* Status Geral */}
            <div className={`mb-4 p-3 rounded-lg ${
              analysisResults.severity === "high"
                ? "bg-red-500/20 border-red-500/40"
                : analysisResults.severity === "moderate"
                  ? "bg-yellow-500/20 border-yellow-500/40"
                  : "bg-green-500/20 border-green-500/40"
            }`}>
              <h4 className="font-semibold text-white">
                Status: {analysisResults.severity.toUpperCase()}
              </h4>
            </div>

            {/* Correlações e Diagnósticos */}
            <div className="space-y-4">
              {analysisResults.correlations.map((correlation, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${
                    correlation.severity === "high"
                      ? "bg-red-500/20 border-red-500/40"
                      : correlation.severity === "moderate"
                        ? "bg-yellow-500/20 border-yellow-500/40"
                        : "bg-green-500/20 border-green-500/40"
                  }`}
                >
                  <h4 className="font-semibold text-white">
                    {correlation.title}
                  </h4>
                  <p className="text-white/80">{correlation.description}</p>
                  {correlation.recommendation && (
                    <p className="mt-2 text-[#ff3366]/80">
                      Recomendação: {correlation.recommendation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="p-4 rounded-lg bg-blue-500/20 border border-blue-500/40">
            <p className="text-sm text-blue-400">
              ⚠️ Esta análise é baseada em diretrizes médicas atuais e serve apenas como referência.
              Consulte sempre um profissional de saúde para diagnóstico e tratamento adequados.
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
